import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Link, Navigate, NavLink, useLocation, useParams, useSearchParams } from "react-router";
import { WaitlistForm } from "../pages/landing.tsx";
import { useMe } from "../session.ts";
import { cx } from "../ui.tsx";
import {
  CUSTOMERS,
  type Entry,
  FAIR_USE,
  INDUSTRIES,
  PRICE,
  PRODUCTS,
  SOLUTIONS,
  VENDORS,
  type Vendor,
  type Visual,
} from "./content.ts";
import { type Block, POSTS, type Post, postPath } from "./posts.ts";
import {
  ChecksCard,
  ClauseCard,
  CreditMemo,
  DeadlineCard,
  PortfolioCard,
  PriceBreakdown,
  RecoveredCard,
  ScorecardCard,
  SmallPlanCard,
  SortedCard,
} from "./visuals.tsx";

/**
 * The public site. One idea throughout: your contract already promises a credit; ninekeep finds
 * it (the highlighter), claims it (the credit memo) and charges one simple price. Campaign
 * landing pages (/welcome, /partners) stay separate and focused on the signup form.
 */

const SECTIONS = {
  product: {
    label: "Product",
    path: "/product",
    items: PRODUCTS,
    intro: "Everything ninekeep does, from contract to credit.",
    lead: "Each part has one purpose: read what your vendors promised, check whether they kept it, and get you paid when they didn't.",
  },
  solutions: {
    label: "Solutions",
    path: "/solutions",
    items: SOLUTIONS,
    intro: "The problems ninekeep solves.",
    lead: "Money your contracts already promise, deadlines nobody tracks, and renewals negotiated without the facts.",
  },
  industries: {
    label: "Industries",
    path: "/industries",
    items: INDUSTRIES,
    intro: "Every industry pays for software that goes down.",
    lead: "Whatever you make or sell, your vendors promised uptime. Here's where ninekeep fits in yours.",
  },
  customers: {
    label: "Teams",
    path: "/customers",
    items: CUSTOMERS,
    intro: "Built for the teams who pay the bills.",
    lead: "Finance sees the money, procurement gets the leverage, IT gets the record. Each team gets what it needs from the same claims.",
  },
} as const;
type SectionKey = keyof typeof SECTIONS;

/** The header's menus: few links, each with a line on why you'd open it. */
type MenuLink = [to: string, title: string, why?: string];
const MENUS: { label: string; cols: { title?: string; links: MenuLink[] }[]; more?: MenuLink }[] = [
  {
    label: "Product",
    cols: [
      {
        links: [
          ["/how-it-works", "How it works", "From contract to money back, in three steps"],
          [
            "/product/contract-reader",
            "Contract reader",
            "What each vendor promised, and what a miss is worth",
          ],
          [
            "/product/vendor-watch",
            "Vendor monitoring",
            "Checked against what your contract promised",
          ],
          ["/product/claims", "Claims", "Sent on time, followed until you're paid"],
        ],
      },
    ],
    more: ["/product", "All features"],
  },
  {
    label: "Solutions",
    cols: [
      {
        title: "By team",
        links: [
          ["/customers/finance-leaders", "Finance"],
          ["/customers/procurement", "Procurement"],
          ["/customers/it-and-operations", "IT and operations"],
          ["/customers/pe-operating-partners", "Private equity firms"],
        ],
      },
      {
        title: "By need",
        links: [
          ["/solutions/sla-credit-recovery", "Get back what you're owed"],
          ["/solutions/never-miss-a-deadline", "Never miss a deadline"],
          ["/solutions/vendor-renewals", "Better renewal deals"],
        ],
      },
    ],
    more: ["/industries", "By industry"],
  },
  {
    label: "Resources",
    cols: [
      {
        links: POSTS.map((p) => [postPath(p), p.title, p.dek] as MenuLink),
      },
    ],
    more: ["/resources", "All posts"],
  },
];
const FOOTER: [string, [string, string][]][] = [
  [
    "Product",
    [
      ["/how-it-works", "How it works"],
      ["/product", "Features"],
      ["/pricing", "Pricing"],
      ["/supported-vendors", "Vendors"],
    ],
  ],
  [
    "Solutions",
    [
      ["/customers", "By team"],
      ["/solutions", "By need"],
      ["/industries", "By industry"],
    ],
  ],
  [
    "Resources",
    [
      ["/resources", "Blog"],
      ["/resources/glossary", "Glossary"],
    ],
  ],
];

function usePageMeta(title: string, description: string) {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    const before = meta?.getAttribute("content") ?? "";
    meta?.setAttribute("content", description);
    return () => {
      document.title = "ninekeep";
      meta?.setAttribute("content", before);
    };
  }, [title, description]);
}

const navCls =
  "whitespace-nowrap rounded-chip px-3 py-2 text-sm font-medium text-muted hover:bg-surface-2 hover:text-ink";

function Menu({ m }: { m: (typeof MENUS)[number] }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const loc = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: close on navigation
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className={cx(navCls, "inline-flex items-center gap-1", open && "bg-surface-2 text-ink")}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen((o) => !o)}
      >
        {m.label}
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          aria-hidden
          className={cx("opacity-60 transition-transform", open && "rotate-180")}
        >
          <path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.4" />
        </svg>
      </button>
      {open && (
        <div
          id={id}
          className={cx(
            "absolute top-full left-0 z-30 mt-2 rounded-card border border-line bg-surface p-2 shadow-pop",
            m.cols.length > 1 ? "w-[30rem]" : "w-80",
          )}
        >
          <div className={cx("grid gap-1", m.cols.length > 1 && "grid-cols-2")}>
            {m.cols.map((c) => (
              <div key={c.title ?? "all"} className="flex min-w-0 flex-col">
                {c.title && <span className="px-3 pt-2 pb-1 eyebrow">{c.title}</span>}
                {c.links.map(([to, t, why]) => (
                  <Link key={to} to={to} className="rounded-chip px-3 py-2 hover:bg-surface-2">
                    <span className="block text-sm font-medium">{t}</span>
                    {why && <span className="block text-xs text-muted">{why}</span>}
                  </Link>
                ))}
              </div>
            ))}
          </div>
          {m.more && (
            <Link
              to={m.more[0]}
              className="mt-1 block rounded-chip border-t border-line px-3 pt-2.5 pb-2 text-sm font-semibold text-accent hover:bg-surface-2"
            >
              {m.more[1]} →
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

const PrimaryLink = ({ to, children, big }: { to: string; children: ReactNode; big?: boolean }) => (
  <Link
    to={to}
    className={cx(
      "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-control bg-ink font-semibold text-bg transition-transform hover:-translate-y-0.5",
      big ? "h-12 px-6 text-base" : "h-9 px-4 text-sm",
    )}
  >
    {children}
  </Link>
);

function Header() {
  const { data: me } = useMe();
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: close on navigation
  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);
  const account: [string, string] = me?.user ? ["/", "Open app"] : ["/signin", "Sign in"];
  return (
    <header className="sticky top-0 z-20 border-b border-line/70 bg-bg/85 backdrop-blur">
      <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
        <Link to="/" className="flex items-center gap-2">
          <img src="/favicon.svg" alt="" className="size-7" />
          <span className="font-display text-xl font-bold tracking-tight">ninekeep</span>
        </Link>
        <nav className="hidden items-center gap-0.5 lg:flex" aria-label="Main">
          {MENUS.map((m) => (
            <Menu key={m.label} m={m} />
          ))}
          <NavLink
            to="/pricing"
            className={({ isActive }) => cx(navCls, isActive && "bg-surface-2 text-ink")}
          >
            Pricing
          </NavLink>
        </nav>
        <div className="flex items-center gap-1">
          <Link to={account[0]} className={cx(navCls, "hidden sm:block")}>
            {account[1]}
          </Link>
          <PrimaryLink to="/waitlist">Get started</PrimaryLink>
          <button
            type="button"
            className="ml-1 rounded-chip border border-line px-3 py-1.5 text-sm lg:hidden"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            Menu
          </button>
        </div>
      </div>
      {open && (
        <nav
          className="max-h-[75dvh] overflow-y-auto border-t border-line bg-surface lg:hidden"
          aria-label="Main"
        >
          <div className="mx-auto grid max-w-6xl gap-6 px-4 py-5 sm:grid-cols-2 sm:px-6">
            <div className="flex flex-col">
              {(
                [["/pricing", "Pricing"], ["/waitlist", "Get started"], account] as [
                  string,
                  string,
                ][]
              ).map(([to, l]) => (
                <Link key={to + l} to={to} className="py-1.5 font-display text-lg font-semibold">
                  {l}
                </Link>
              ))}
            </div>
            {MENUS.map((m) => (
              <div key={m.label} className="flex flex-col">
                <span className="pb-1 eyebrow">{m.label}</span>
                {[...m.cols.flatMap((c) => c.links), ...(m.more ? [m.more] : [])].map(([to, t]) => (
                  <Link key={to} to={to} className="py-1 text-sm">
                    {t}
                  </Link>
                ))}
              </div>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-band text-band-ink">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:grid-cols-2 sm:px-6 lg:grid-cols-[1.6fr_1fr_1fr_1fr]">
        <div className="flex flex-col items-start gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.svg" alt="" className="size-6" />
            <span className="font-display text-lg font-bold">ninekeep</span>
          </Link>
          <p className="max-w-xs text-sm text-band-muted">
            Your software vendors owe you money. ninekeep gets it back.
          </p>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/waitlist" className="font-semibold text-hl">
              Get started →
            </Link>
            <Link to="/signin" className="text-band-muted hover:text-band-ink">
              Sign in
            </Link>
          </div>
        </div>
        {FOOTER.map(([h, links]) => (
          <nav key={h} className="flex flex-col gap-1.5 text-sm" aria-label={h}>
            <span className="mb-1 font-mono text-label tracking-label text-band-muted uppercase">
              {h}
            </span>
            {links.map(([to, l]) => (
              <Link key={to} to={to} className="text-band-ink/85 hover:text-band-ink">
                {l}
              </Link>
            ))}
          </nav>
        ))}
      </div>
      <div className="mx-auto max-w-6xl px-4 pb-8 text-xs text-band-muted sm:px-6">
        © {new Date().getFullYear()} ninekeep. Vendor names belong to their owners and appear only
        to describe contracts ninekeep can read; no vendor endorses ninekeep.
      </div>
    </footer>
  );
}

export function SiteLayout({ children }: { children: ReactNode }) {
  const loc = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: back to the top on each new page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [loc.pathname]);
  return (
    <div className="flex min-h-dvh flex-col">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <Header />
      <main id="main" tabIndex={-1} className="flex-1 focus:outline-none">
        {children}
      </main>
      <Footer />
    </div>
  );
}

const Wrap = ({ children, className }: { children: ReactNode; className?: string }) => (
  <div className={cx("mx-auto max-w-6xl px-4 sm:px-6", className)}>{children}</div>
);

const Kicker = ({ children }: { children: ReactNode }) => (
  <p className="font-mono text-xs font-medium tracking-kicker text-accent uppercase">{children}</p>
);

const Display = ({
  children,
  as: As = "h2",
  className,
}: {
  children: ReactNode;
  as?: "h1" | "h2";
  className?: string;
}) => (
  <As
    className={cx(
      "font-display font-bold tracking-tight text-balance",
      As === "h1"
        ? "text-5xl leading-display sm:text-6xl lg:text-7xl"
        : "text-3xl leading-tight sm:text-4xl",
      className,
    )}
  >
    {children}
  </As>
);

function Cta() {
  return (
    <section className="bg-band text-band-ink">
      <Wrap className="flex flex-col items-start gap-6 py-16 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <h2 className="font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl">
            See what your vendors <span className="hl-word">owe you</span>.
          </h2>
          <p className="mt-3 max-w-xl text-band-muted">
            Start with a free audit: up to 5 contracts and the last 12 months, with what each vendor
            owed you and the claims ready to send.
          </p>
        </div>
        <Link
          to="/waitlist"
          className="inline-flex h-12 shrink-0 items-center rounded-control bg-hl px-6 font-semibold text-hl-ink transition-transform hover:-translate-y-0.5"
        >
          Get started
        </Link>
      </Wrap>
    </section>
  );
}

const STEPS = [
  [
    "Read",
    "Upload your vendor contracts. ninekeep finds what each vendor promised, what a miss is worth, and the deadline to ask.",
  ],
  ["Monitor", "Every month, ninekeep checks each vendor against what it promised you."],
  [
    "Claim",
    "When a vendor falls short, ninekeep writes the claim, sends it on time, and follows it until you're paid.",
  ],
] as const;

/**
 * The product demo, recorded from the real app (scripts/demo): a sample customer whose vendors'
 * October 2026 outages are real, as their status pages published them.
 */
function DemoVideo() {
  return (
    <figure className="overflow-hidden rounded-panel border border-line bg-band shadow-hero">
      <video
        className="block aspect-[16/9] w-full"
        src="/demo/ninekeep-demo.mp4"
        poster="/demo/ninekeep-demo-poster.jpg"
        controls
        playsInline
        preload="none"
      >
        <track kind="captions" />
      </video>
      <figcaption className="px-5 py-3 text-sm text-band-muted">
        Real outages at GitHub and Snowflake in October 2026, as their status pages published them.
        The customer and its contracts are a sample.
      </figcaption>
    </figure>
  );
}

function Steps() {
  return (
    <ol className="grid gap-px overflow-hidden rounded-panel border border-line bg-line md:grid-cols-3">
      {STEPS.map(([t, b], i) => (
        <li key={t} className="flex flex-col gap-3 bg-surface p-7">
          <span className="font-mono text-sm text-faint">0{i + 1}</span>
          <h3 className="font-display text-3xl font-bold tracking-tight">{t}</h3>
          <p className="text-muted">{b}</p>
        </li>
      ))}
    </ol>
  );
}

function VendorStrip() {
  return (
    <div className="flex flex-wrap gap-2">
      {VENDORS.map((v) => (
        <Link
          key={v.slug}
          to={`/supported-vendors/${v.slug}`}
          className="rounded-full border border-line bg-surface px-3.5 py-1.5 text-sm transition-colors hover:border-ink"
        >
          {v.name}
        </Link>
      ))}
    </div>
  );
}

function PriceLine() {
  return (
    <p className="font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl">
      <span className="marker">${PRICE.perVendor}</span> per vendor a month, plus{" "}
      <span className="marker">{PRICE.successFee}%</span> of what we recover.
    </p>
  );
}

/** Opens in place: the details belong on this page, not behind another click away. */
function Accordion({ items }: { items: readonly Entry[] }) {
  return (
    <div className="divide-y divide-line border-y border-line">
      {items.map((i) => (
        <details key={i.slug} className="group">
          <summary className="flex cursor-pointer list-none items-baseline justify-between gap-6 py-4 [&::-webkit-details-marker]:hidden">
            <span className="min-w-0">
              <span className="block font-display text-xl font-semibold tracking-tight group-open:text-accent">
                {i.title}
              </span>
              <span className="block text-sm text-muted">{i.summary}</span>
            </span>
            <span
              className="flex size-7 shrink-0 items-center justify-center rounded-full border border-line text-muted transition-transform group-open:rotate-45 group-open:border-ink group-open:text-ink"
              aria-hidden
            >
              +
            </span>
          </summary>
          <div className="flex flex-col gap-5 pb-6">
            <p className="max-w-2xl text-muted">{i.lead}</p>
            {i.sections.map((sec) => (
              <div key={sec.heading} className="min-w-0">
                <h3 className="font-semibold">{sec.heading}</h3>
                {sec.text && <p className="mt-1 max-w-2xl text-muted">{sec.text}</p>}
                {sec.bullets && (
                  <ul className="mt-2 flex flex-col gap-1.5">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex gap-3">
                        <span className="mt-2 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </details>
      ))}
    </div>
  );
}

const checkedOn = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

/** What ninekeep reads in a vendor's agreement when the vendor doesn't publish one table for everyone. */
function ContractTerms({ v }: { v: Vendor }) {
  const short = v.name.split(" (")[0];
  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-2xl text-muted">
        {short}'s terms depend on the product and plan you bought, so the ones that count are in
        your contract. ninekeep reads:
      </p>
      <ul className="grid gap-2 sm:grid-cols-2">
        {[
          `The uptime promise for the ${short} products you bought`,
          "What you get back for each miss",
          "The deadline to ask, and where to send the claim",
          "What doesn't count, like planned maintenance",
        ].map((t) => (
          <li key={t} className="flex gap-3">
            <span className="mt-2 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}

function WatchedAt({ v }: { v: Vendor }) {
  return (
    <p className="font-mono text-label text-faint">
      Monitored at{" "}
      <a href={v.status} target="_blank" rel="noreferrer" className="text-accent">
        {v.status.replace(/^https:\/\/(www\.)?/, "")}
      </a>
      , plus your own checks and {v.name.split(" (")[0]}'s incident emails.
    </p>
  );
}

/** Pick a vendor and its terms show right below, so the visitor stays on the page. */
function VendorExplorer() {
  const [slug, setSlug] = useState(VENDORS[0]!.slug);
  const v = VENDORS.find((x) => x.slug === slug)!;
  const p = v.published;
  return (
    <div className="flex flex-col gap-6">
      <fieldset className="m-0 flex min-w-0 flex-wrap gap-2 border-0 p-0">
        <legend className="sr-only">Example vendors</legend>
        {VENDORS.map((x) => (
          <button
            key={x.slug}
            type="button"
            aria-pressed={x.slug === slug}
            onClick={() => setSlug(x.slug)}
            className={cx(
              "rounded-full border px-3.5 py-1.5 text-sm transition-colors",
              x.slug === slug
                ? "border-ink bg-ink font-medium text-bg"
                : "border-line bg-surface hover:border-ink",
            )}
          >
            {x.name}
          </button>
        ))}
      </fieldset>
      <div className="memo rounded-b-card px-6 pt-8 pb-6 shadow-hero" aria-live="polite">
        <div className="flex flex-wrap items-baseline justify-between gap-2 eyebrow">
          <span>
            {v.name} · {v.category}
          </span>
          {p && (
            <a
              href={p.source}
              target="_blank"
              rel="noreferrer"
              className="tracking-normal normal-case text-accent"
            >
              Published SLA ↗
            </a>
          )}
        </div>
        {p ? (
          <>
            <dl className="mt-5 grid gap-x-6 gap-y-3 sm:grid-cols-[140px_1fr]">
              <dt className="font-mono text-xs text-muted uppercase">Promise</dt>
              <dd>
                <mark className="mark-clause">{p.promise}</mark>
              </dd>
              <dt className="font-mono text-xs text-muted uppercase">Credits</dt>
              <dd>{p.credits.join(" · ")}</dd>
              <dt className="font-mono text-xs text-muted uppercase">Deadline</dt>
              <dd>
                <mark className="mark-clause">{p.deadline}</mark>
              </dd>
              <dt className="font-mono text-xs text-muted uppercase">How to claim</dt>
              <dd>{p.howToClaim}</dd>
            </dl>
            {p.note && <p className="mt-4 text-sm text-muted">{p.note}</p>}
            <p className="mt-4 font-mono text-label text-faint">
              As published, checked {checkedOn(p.checked)}. Negotiated contracts often differ;
              ninekeep uses yours.
            </p>
          </>
        ) : (
          <div className="mt-4">
            <ContractTerms v={v} />
          </div>
        )}
        <div className="mt-4">
          <WatchedAt v={v} />
        </div>
      </div>
    </div>
  );
}

function LinkList({ items, base }: { items: readonly Entry[]; base: string }) {
  return (
    <ul className="divide-y divide-line border-y border-line">
      {items.map((i) => (
        <li key={i.slug}>
          <Link
            to={`${base}/${i.slug}`}
            className="group flex items-baseline justify-between gap-6 py-4"
          >
            <span className="min-w-0">
              <span className="block font-display text-xl font-semibold tracking-tight group-hover:text-accent">
                {i.title}
              </span>
              <span className="block text-sm text-muted">{i.summary}</span>
            </span>
            <span className="text-faint transition-transform group-hover:translate-x-1" aria-hidden>
              →
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

// ---------------------------------------------------------------------------------- pages

export function SiteHome() {
  usePageMeta(
    "ninekeep: get back what your vendors owe you",
    "When a software vendor misses its uptime promise, your contract often owes you money back. ninekeep finds it, claims it, and charges one simple price.",
  );
  return (
    <SiteLayout>
      <section className="relative overflow-hidden bg-band text-band-ink">
        <Wrap className="grid items-center gap-14 pt-16 pb-24 lg:grid-cols-[1.25fr_1fr] lg:pt-24">
          <div className="rise min-w-0">
            <p className="font-mono text-xs font-medium tracking-kicker text-hl uppercase">
              SLA credit recovery
            </p>
            <Display as="h1" className="mt-5">
              Your software vendors <span className="hl-word">owe you money</span>. ninekeep gets it
              back.
            </Display>
            <p className="mt-6 max-w-xl text-lg text-band-muted">
              When a software vendor goes down, your contract usually gives you money off your next
              bill, a service credit. Almost nobody claims it. ninekeep reads your contracts,
              monitors your vendors and claims it before the deadline, with the math and proof.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-5">
              <Link
                to="/waitlist"
                className="inline-flex h-12 items-center justify-center rounded-control bg-hl px-6 text-base font-semibold whitespace-nowrap text-hl-ink transition-transform hover:-translate-y-0.5"
              >
                Get started
              </Link>
              <Link
                to="/how-it-works"
                className="font-semibold text-band-ink underline decoration-white/30 decoration-2 underline-offset-4 hover:decoration-hl"
              >
                See how it works
              </Link>
            </div>
          </div>
          <CreditMemo />
        </Wrap>
      </section>

      <section className="border-y border-line bg-surface">
        <Wrap className="py-20">
          <Kicker>How it works</Kicker>
          <Display className="mt-4 max-w-2xl">Read. Monitor. Claim.</Display>
          <div className="mt-10">
            <Steps />
          </div>
        </Wrap>
      </section>

      <Wrap className="grid items-center gap-12 py-20 lg:grid-cols-2">
        <div className="min-w-0">
          <Kicker>What we look for</Kicker>
          <Display className="mt-4">Every contract hides the same three lines.</Display>
          <p className="mt-5 max-w-lg text-muted">
            What the vendor promised, what you get back when it misses, and the deadline to ask.
            Most people stop at the first. ninekeep reads all three, in every contract.
          </p>
          <Link
            to="/resources/read-an-sla-credit-clause"
            className="mt-6 inline-block font-semibold text-accent"
          >
            How to read a credit clause →
          </Link>
        </div>
        <ClauseCard />
      </Wrap>

      <section className="bg-band text-band-ink">
        <Wrap className="grid gap-10 py-20 lg:grid-cols-[1fr_1.4fr] lg:items-center">
          <div className="min-w-0">
            <p className="font-mono text-xs font-medium tracking-kicker text-hl uppercase">
              Pricing
            </p>
            <p className="mt-4 font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl">
              ${PRICE.perVendor} per vendor a month, plus {PRICE.successFee}% of what we recover.
            </p>
            <p className="mt-4 text-band-muted">
              The same price for every company. You start with a free audit, so you see what you're
              owed before you pay anything.
            </p>
            <Link to="/pricing" className="mt-6 inline-block font-semibold text-hl">
              Pricing details →
            </Link>
          </div>
          <PriceBreakdown />
        </Wrap>
      </section>

      <Wrap className="py-20">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.4fr]">
          <div className="min-w-0">
            <Kicker>Who it's for</Kicker>
            <Display className="mt-4">Built for the people who pay the invoices.</Display>
          </div>
          <Accordion items={CUSTOMERS} />
        </div>
      </Wrap>

      <section className="border-t border-line bg-surface">
        <Wrap className="py-16">
          <h2 className="font-display text-2xl font-bold tracking-tight">
            Works with the vendors you already pay
          </h2>
          <p className="mt-2 text-muted">
            Any vendor whose contract promises uptime. Pick one to see what ninekeep reads:
          </p>
          <div className="mt-6">
            <VendorExplorer />
          </div>
        </Wrap>
      </section>

      <Cta />
    </SiteLayout>
  );
}

/** Who does each step: the person (yellow) or ninekeep (teal). */
const Who = ({ you }: { you?: boolean }) => (
  <span
    className={cx(
      "inline-flex h-6 items-center rounded-full px-2.5 font-mono text-label font-semibold tracking-label uppercase",
      you ? "bg-hl text-hl-ink" : "bg-accent-soft text-accent",
    )}
  >
    {you ? "You" : "ninekeep"}
  </span>
);

/** The small thing each step makes, drawn in the site's paper-and-mono style. */
const Paper = ({ children }: { children: ReactNode }) => (
  <div className="mt-4 rounded-card border border-line bg-bg p-3 font-mono text-xs leading-relaxed text-muted">
    {children}
  </div>
);

const JOURNEY: { you?: boolean; title: string; body: string; art: ReactNode }[] = [
  {
    you: true,
    title: "Upload your contracts",
    body: "Agreements, order forms, SLA addendums. PDF, Word or text.",
    art: (
      <div className="flex flex-col gap-1.5">
        {["Master agreement.pdf", "Order form 2026.docx", "SLA addendum.pdf"].map((f) => (
          <span key={f} className="flex items-center gap-2">
            <span className="size-2 rounded-sm bg-line-strong" aria-hidden />
            {f}
          </span>
        ))}
      </div>
    ),
  },
  {
    title: "Reads every promise",
    body: "What each vendor promised, what a miss pays back, and the last day to ask.",
    art: (
      <>
        <p>
          "available <span className="bg-hl px-0.5 text-hl-ink">at least 99.9%</span> each month"
        </p>
        <p className="mt-1">
          credit: <span className="bg-hl px-0.5 text-hl-ink">10% of the fee</span>
        </p>
        <p className="mt-1">
          ask within <span className="bg-hl px-0.5 text-hl-ink">30 days</span>
        </p>
      </>
    ),
  },
  {
    you: true,
    title: "Approve what it found",
    body: "Check it against the contract's own words. Nothing counts until you approve.",
    art: (
      <span className="flex items-center gap-2 text-ink">
        <span className="flex size-4 items-center justify-center rounded-chip bg-ink text-label text-bg">
          ✓
        </span>
        Terms approved
      </span>
    ),
  },
  {
    title: "Watches every vendor",
    body: "Status pages, its own uptime checks and outage emails, measured against your contract.",
    art: (
      <div className="flex flex-col gap-1.5">
        {[
          ["Cloud hosting", "100%", true],
          ["Ticketing", "99.98%", true],
          ["Data warehouse", "99.32%", false],
        ].map(([n, v, ok]) => (
          <span key={n as string} className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2">
              <span className={cx("size-2 rounded-full", ok ? "bg-ok" : "bg-danger")} aria-hidden />
              {n}
            </span>
            <span className={ok ? "" : "font-semibold text-danger"}>{v}</span>
          </span>
        ))}
      </div>
    ),
  },
  {
    title: "Works out the credit and writes the claim",
    body: "The math step by step, the proof attached, ready well before the deadline.",
    art: (
      <>
        <p>99.32% is below 99.9%</p>
        <p className="mt-1">10% × $10,000 = $1,000.00</p>
        <p className="mt-1 text-ink">Proof attached · due in 21 days</p>
      </>
    ),
  },
  {
    you: true,
    title: "Check it and send",
    body: "One click. Or let small claims go on their own after a wait you choose.",
    art: (
      <span className="inline-flex h-7 items-center rounded-control bg-ink px-3 font-sans text-xs font-semibold text-bg">
        Send claim
      </span>
    ),
  },
  {
    title: "Follows it until you're paid",
    body: "Reads the vendor's replies, answers pushback, and confirms the credit lands.",
    art: (
      <>
        <p>Vendor replied: credit approved</p>
        <p className="mt-1 font-display text-lg font-bold text-ink">
          <span className="marker">−$1,000.00</span>
        </p>
        <p>on your next invoice</p>
      </>
    ),
  },
];

/**
 * The journey as a flowchart: two lanes (you, ninekeep), the monthly check that loops until a
 * vendor misses, and the path from a miss to a credit on your invoice. Wide screens only; a
 * phone gets the step cards as a timeline.
 */
function FlowChart() {
  const X = (c: number) => 140 + c * 126; // column centers
  const YOU = 104;
  const NK = 312;
  const W = 108;
  const H = 92;
  let n = 0;
  const node = (c: number, y: number, lines: string[], tone: "you" | "nk" | "end") => {
    const step = tone === "end" ? null : ++n;
    const top = y - H / 2;
    return (
      <g key={`${c}-${y}`}>
        <rect
          x={X(c) - W / 2}
          y={top}
          width={W}
          height={H}
          rx={14}
          className={tone === "end" ? "fill-hl" : "fill-surface"}
        />
        {step !== null && (
          <>
            <circle
              cx={X(c) - W / 2 + 18}
              cy={top + 18}
              r={10}
              className={tone === "you" ? "fill-hl" : "fill-accent-soft"}
            />
            <text
              x={X(c) - W / 2 + 18}
              y={top + 22}
              textAnchor="middle"
              className={cx("font-mono", tone === "you" ? "fill-hl-ink" : "fill-accent")}
              fontSize={10}
              fontWeight={700}
            >
              {step}
            </text>
          </>
        )}
        <text
          x={X(c)}
          y={top + (step === null ? H / 2 - ((lines.length - 1) * 15) / 2 + 5 : 52)}
          textAnchor="middle"
          className={cx("font-display", tone === "end" ? "fill-hl-ink" : "fill-ink")}
          fontSize={tone === "end" ? 14 : 13}
          fontWeight={700}
        >
          {lines.map((l, i) => (
            <tspan key={l} x={X(c)} dy={i === 0 ? 0 : 15}>
              {l}
            </tspan>
          ))}
        </text>
      </g>
    );
  };
  // Out of the top or bottom of one node, across to the side of the next, in the other lane.
  const elbow = (c1: number, y1: number, c2: number, y2: number) =>
    `M${X(c1)} ${y1 < y2 ? y1 + H / 2 : y1 - H / 2} V${y2} H${X(c2) - W / 2 - 5}`;
  const D = 48; // diamond half-size
  return (
    <figure className="overflow-hidden rounded-panel bg-band p-5 shadow-hero sm:p-6">
      <svg
        viewBox="0 0 1260 440"
        className="block h-auto w-full"
        role="img"
        aria-labelledby="flow-title flow-desc"
      >
        <title id="flow-title">How ninekeep gets you your credits</title>
        <desc id="flow-desc">
          You upload contracts. ninekeep reads every promise. You approve. Every month ninekeep
          watches each vendor; if a vendor missed its promise, ninekeep works out the credit and
          writes the claim, you check it and send, ninekeep follows up until the credit is on your
          invoice. If not, it keeps watching.
        </desc>
        <defs>
          <marker
            id="flow-arrow"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="7"
            markerHeight="7"
            orient="auto-start-reverse"
          >
            <path d="M0 0 L10 5 L0 10 z" className="fill-band-muted" />
          </marker>
        </defs>
        {/* Lanes */}
        <rect x={4} y={16} width={1252} height={176} rx={18} className="fill-white/5" />
        <rect x={4} y={208} width={1252} height={224} rx={18} className="fill-white/5" />
        <text x={24} y={42} className="fill-hl font-mono" fontSize={11} letterSpacing={2.5}>
          YOU
        </text>
        <text
          x={24}
          y={234}
          className="fill-band-muted font-mono"
          fontSize={11}
          letterSpacing={2.5}
        >
          NINEKEEP
        </text>
        {/* Arrows */}
        <g
          className="stroke-band-muted"
          fill="none"
          strokeWidth={1.75}
          markerEnd="url(#flow-arrow)"
        >
          <path d={elbow(0, YOU, 1, NK)} />
          <path d={elbow(1, NK, 2, YOU)} />
          <path d={elbow(2, YOU, 3, NK)} />
          <path d={`M${X(3) + W / 2} ${NK} H${X(4) - D - 5}`} />
          <path d={`M${X(4) + D} ${NK} H${X(5) - W / 2 - 5}`} />
          <path d={elbow(5, NK, 6, YOU)} />
          <path d={elbow(6, YOU, 7, NK)} />
          <path d={elbow(7, NK, 8, YOU)} />
          {/* No miss: back to watching, next month */}
          <path
            d={`M${X(4)} ${NK + D} V${NK + 88} H${X(3)} V${NK + H / 2 + 5}`}
            strokeDasharray="5 6"
          />
        </g>
        <text
          x={(X(4) + D + X(5) - W / 2) / 2}
          y={NK - 10}
          textAnchor="middle"
          className="fill-hl font-mono"
          fontSize={11}
          fontWeight={700}
        >
          YES
        </text>
        <text
          x={(X(3) + X(4)) / 2}
          y={NK + 108}
          textAnchor="middle"
          className="fill-band-muted font-mono"
          fontSize={11}
          letterSpacing={1}
        >
          NO · CHECK AGAIN NEXT MONTH
        </text>
        {/* Nodes */}
        {node(0, YOU, ["Upload your", "contracts"], "you")}
        {node(1, NK, ["Reads every", "promise"], "nk")}
        {node(2, YOU, ["Approve what", "it found"], "you")}
        {node(3, NK, ["Watches", "each vendor,", "monthly"], "nk")}
        <g>
          <path
            d={`M${X(4)} ${NK - D} L${X(4) + D} ${NK} L${X(4)} ${NK + D} L${X(4) - D} ${NK} Z`}
            className="fill-surface"
          />
          <text
            x={X(4)}
            y={NK - 3}
            textAnchor="middle"
            className="fill-ink font-display"
            fontSize={12.5}
            fontWeight={700}
          >
            <tspan x={X(4)}>Vendor</tspan>
            <tspan x={X(4)} dy={15}>
              missed?
            </tspan>
          </text>
        </g>
        {node(5, NK, ["Works out", "the credit,", "writes claim"], "nk")}
        {node(6, YOU, ["Check it", "and send"], "you")}
        {node(7, NK, ["Follows up", "until paid"], "nk")}
        {node(8, YOU, ["Credit on", "your invoice"], "end")}
      </svg>
    </figure>
  );
}

/** The whole journey at a glance: who does what, from contract to credit. */
function Journey() {
  return (
    <ol className="relative grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {JOURNEY.map((s, i) => (
        <li
          key={s.title}
          className={cx(
            "relative flex flex-col rounded-panel border p-5",
            s.you ? "border-line bg-surface" : "border-accent/30 bg-surface",
            i === JOURNEY.length - 1 && "border-2 border-ink lg:col-span-1",
          )}
        >
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-sm text-faint">0{i + 1}</span>
            <Who you={s.you} />
          </div>
          <h3 className="mt-3 font-display text-xl font-bold tracking-tight text-balance">
            {s.title}
          </h3>
          <p className="mt-1.5 text-sm text-muted">{s.body}</p>
          <div className="mt-auto">
            <Paper>{s.art}</Paper>
          </div>
          {i < JOURNEY.length - 1 && (
            <span
              className="absolute -bottom-3.5 left-1/2 z-10 flex size-7 -translate-x-1/2 items-center justify-center rounded-full border border-line bg-bg text-faint sm:hidden"
              aria-hidden
            >
              ↓
            </span>
          )}
        </li>
      ))}
      <li className="flex flex-col justify-center rounded-panel bg-band p-6 text-band-ink">
        <p className="font-mono text-xs tracking-label text-band-muted uppercase">Your part</p>
        <p className="mt-2 font-display text-2xl font-bold tracking-tight text-balance">
          Three steps. ninekeep does <span className="hl-word">the rest</span>.
        </p>
        <p className="mt-2 text-sm text-band-muted">
          A few minutes when you start, then one look before each claim goes out.
        </p>
      </li>
    </ol>
  );
}

export function HowItWorks() {
  usePageMeta(
    "How ninekeep works",
    "Read your contracts, monitor your vendors, get your money back: how ninekeep works.",
  );
  const detail: [string, string, string[]][] = [
    [
      "Read",
      "Upload your contracts as PDF, Word or text. ninekeep lists every promise, in the contract's own words. You check it and approve.",
      [
        "How much uptime, and how fast support answers",
        "The credit for each miss",
        "The deadline to ask, and when the contract renews",
      ],
    ],
    [
      "Monitor",
      "Every month, ninekeep checks each vendor against your contract, not just the vendor's own status page.",
      [
        "The vendor's status page, for the services you pay for",
        "ninekeep's own checks on the service",
        "The vendor's outage emails",
      ],
    ],
    [
      "Claim",
      "When a vendor falls short, ninekeep works out what you're owed, writes the claim with proof, and sends it when you say so.",
      [
        "Every number shown, step by step",
        "Vendor replies read, and pushback answered",
        "Followed until you're paid",
      ],
    ],
  ];
  return (
    <SiteLayout>
      <Wrap className="pt-10 pb-12">
        <SectionTabs />
        <div className="mt-12" />
        <Kicker>How it works</Kicker>
        <Display as="h1" className="mt-5 max-w-4xl">
          From your contract to a <span className="marker">credit on your invoice</span>.
        </Display>
        <p className="mt-4 max-w-2xl text-lg text-muted">
          Vendors call it a service credit: money off your next bill, owed when they miss the uptime
          they promised.
        </p>
      </Wrap>
      <Wrap className="pb-16">
        <div className="hidden lg:block">
          <FlowChart />
          <h2 className="mt-14 mb-5 font-display text-3xl font-bold tracking-tight">
            What each step looks like
          </h2>
        </div>
        <Journey />
        <h2 className="mt-14 mb-5 font-display text-3xl font-bold tracking-tight">
          Watch it, start to finish
        </h2>
        <DemoVideo />
      </Wrap>
      <Wrap className="flex flex-col gap-4 pb-20">
        <h2 className="mb-2 font-display text-3xl font-bold tracking-tight">Each part, up close</h2>
        {detail.map(([t, lead, bullets], i) => (
          <div
            key={t}
            className="grid gap-6 rounded-panel border border-line bg-surface p-7 md:grid-cols-[auto_1fr_1fr] md:items-start"
          >
            <span className="font-mono text-sm text-faint md:pt-3">0{i + 1}</span>
            <div className="min-w-0">
              <h2 className="font-display text-4xl font-bold tracking-tight">{t}</h2>
              <p className="mt-3 text-muted">{lead}</p>
            </div>
            <ul className="flex flex-col gap-2.5 md:pt-2">
              {bullets.map((b) => (
                <li key={b} className="flex gap-3">
                  <span className="mt-2 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
                  {b}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </Wrap>
      <Wrap className="grid gap-10 border-t border-line py-20 md:grid-cols-3">
        {[
          [
            "You approve what goes out",
            "Nothing goes to a vendor unless you say so. You can also let small claims send on their own, after a wait you choose.",
          ],
          [
            "Your data stays yours",
            "Every account signs in with a second step, and each company's data stays private to that company. Single sign-on is included.",
          ],
          [
            "Nothing to install",
            "ninekeep works from your contracts and public information. It never needs access to your systems.",
          ],
        ].map(([t, b]) => (
          <div key={t}>
            <h3 className="font-display text-xl font-semibold tracking-tight">{t}</h3>
            <p className="mt-2 text-muted">{b}</p>
          </div>
        ))}
      </Wrap>
      <Cta />
    </SiteLayout>
  );
}

export function Pricing() {
  usePageMeta(
    "ninekeep pricing",
    `A free audit, then $${PRICE.perVendor} per vendor a month plus ${PRICE.successFee}% of credits recovered.`,
  );
  const faq: [string, string][] = [
    [
      "What does the free audit find?",
      `What each vendor owed you over the last 12 months, from up to ${PRICE.auditContracts} contracts, with the claims ready for anything still in time.`,
    ],
    [
      "When is the 15% charged?",
      "Only when money ninekeep claimed actually comes off your bill. If nothing comes back, you pay nothing extra.",
    ],
    [
      "When can I start?",
      "Today. Tell us about your company and we'll set up your account and your free audit.",
    ],
    [
      "Can I stop after the free audit?",
      "Yes. There's nothing to sign, and the claims it wrote are yours to send.",
    ],
  ];
  return (
    <SiteLayout>
      <Wrap className="pt-16 pb-14">
        <Kicker>Pricing</Kicker>
        <Display as="h1" className="mt-5 max-w-4xl">
          Start with a free audit. Pay only for the vendors we monitor.
        </Display>
        <div className="mt-10 max-w-3xl">
          <PriceLine />
        </div>
      </Wrap>
      <Wrap className="grid gap-4 pb-16 md:grid-cols-2">
        <div className="rounded-panel border border-line bg-surface p-7">
          <span className="font-mono text-sm text-faint">First</span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">Free audit</h2>
          <p className="mt-3 text-muted">
            Up to {PRICE.auditContracts} contracts and the last 12 months: what each vendor owed
            you, with the math and claims ready for anything still in time.
          </p>
          <p className="mt-5 font-display text-4xl font-bold">$0</p>
        </div>
        <div className="rounded-panel border-2 border-ink bg-surface p-7">
          <span className="font-mono text-sm text-faint">Then</span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">Recover</h2>
          <p className="mt-3 text-muted">
            Every vendor monitored. Every claim written, sent and followed. Deadline and renewal
            reminders, single sign-on, everything included.
          </p>
          <p className="mt-5 font-display text-4xl font-bold">
            ${PRICE.perVendor}
            <span className="text-base font-medium text-muted">
              {" "}
              per vendor a month + {PRICE.successFee}%
            </span>
          </p>
        </div>
      </Wrap>
      <Wrap className="grid gap-12 py-20 lg:grid-cols-2">
        <div className="min-w-0">
          <h2 className="font-display text-2xl font-bold tracking-tight">
            Simple, and on your side
          </h2>
          <ul className="mt-6 flex flex-col gap-3">
            {FAIR_USE.map((f) => (
              <li key={f} className="flex gap-3">
                <span className="mt-2 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
                {f}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-faint">You see your price before you pay anything.</p>
        </div>
        <dl className="flex flex-col gap-6">
          {faq.map(([q, a]) => (
            <div key={q}>
              <dt className="font-semibold">{q}</dt>
              <dd className="mt-1 text-muted">{a}</dd>
            </div>
          ))}
        </dl>
      </Wrap>
      <Cta />
    </SiteLayout>
  );
}

export function GetStarted() {
  usePageMeta(
    "Join the waitlist · ninekeep",
    "Join the ninekeep waitlist: we're letting companies in a few at a time, each with a free audit of what its software vendors owe it.",
  );
  const [params] = useSearchParams();
  const audience = params.get("for") === "partner" ? "partner" : "company";
  return (
    <SiteLayout>
      <Wrap className="grid gap-12 py-16 lg:grid-cols-[1fr_1.1fr]">
        <div className="min-w-0">
          <Kicker>Waitlist</Kicker>
          <Display as="h1" className="mt-5">
            Join the <span className="marker">waitlist</span>.
          </Display>
          <p className="mt-5 max-w-xl text-lg text-muted">
            ninekeep is opening to a few companies at a time. Join free, and when it's your turn
            you'll start with a free audit of what your vendors owe you.
          </p>
          <ol className="mt-10 flex flex-col gap-6">
            {[
              ["Join the waitlist", "Free. Under a minute, with your work email."],
              [
                "We let you in",
                "When it's your turn, we set up your account and email you a link to sign in.",
              ],
              [
                "Get your free audit",
                `Send up to ${PRICE.auditContracts} contracts. See what each vendor owed you in the last 12 months.`,
              ],
            ].map(([t, b], i) => (
              <li key={t} className="flex gap-5">
                <span className="font-mono text-sm text-faint">0{i + 1}</span>
                <div className="min-w-0">
                  <h2 className="font-display text-2xl font-bold tracking-tight">{t}</h2>
                  <p className="text-muted">{b}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
        <div className="min-w-0 lg:sticky lg:top-24 lg:self-start">
          <WaitlistForm audience={audience} />
        </div>
      </Wrap>
    </SiteLayout>
  );
}

/** Hop between the site's sections from any of them. */
const SECTION_TABS: [to: string, label: string][] = [
  ["/how-it-works", "How it works"],
  ["/product", "Product"],
  ["/solutions", "By need"],
  ["/customers", "By team"],
  ["/industries", "By industry"],
  ["/supported-vendors", "Vendors"],
];
function SectionTabs() {
  return (
    <nav aria-label="Sections" className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0">
      <ul className="flex w-max gap-1.5">
        {SECTION_TABS.map(([to, label]) => (
          <li key={to}>
            <NavLink
              to={to}
              className={({ isActive }) =>
                cx(
                  "inline-flex h-8 items-center rounded-full border px-3.5 text-sm font-medium whitespace-nowrap transition-colors",
                  isActive
                    ? "border-ink bg-ink text-bg"
                    : "border-line bg-surface text-muted hover:border-ink hover:text-ink",
                )
              }
            >
              {label}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/** What an entry covers, in a few short lines for its card. */
const pointsOf = (e: Entry) =>
  e.sections.flatMap((s) => s.bullets ?? (s.text ? [s.text] : [])).slice(0, 3);

function EntryCards({ items, base }: { items: readonly Entry[]; base: string }) {
  const [first, ...rest] = items;
  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {first && (
        <Link
          to={`${base}/${first.slug}`}
          className="group grid gap-x-12 gap-y-6 rounded-panel bg-band p-7 text-band-ink sm:p-9 md:col-span-2 lg:col-span-3 lg:grid-cols-2"
        >
          <div className="min-w-0">
            <span className="font-mono text-sm text-band-muted">01</span>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl">
              {first.title}
            </h2>
            <p className="mt-3 max-w-xl text-band-muted">{first.lead}</p>
          </div>
          <div className="flex min-w-0 flex-col lg:pt-8">
            <ul className="flex flex-col gap-2.5">
              {pointsOf(first).map((b) => (
                <li key={b} className="flex gap-3">
                  <span className="mt-2.5 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
                  {b}
                </li>
              ))}
            </ul>
            <span className="mt-auto pt-6 font-semibold text-hl">
              Read more{" "}
              <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
            </span>
          </div>
        </Link>
      )}
      {rest.map((e, i) => (
        <Link
          key={e.slug}
          to={`${base}/${e.slug}`}
          className="group flex flex-col rounded-panel border border-line bg-surface p-6 transition-colors hover:border-ink"
        >
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-sm text-faint">{String(i + 2).padStart(2, "0")}</span>
            {e.coming && (
              <span className="rounded-full bg-surface-2 px-2.5 py-0.5 font-mono text-label tracking-label text-muted uppercase">
                Coming
              </span>
            )}
          </div>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-balance group-hover:text-accent">
            {e.title}
          </h2>
          <p className="mt-2 text-muted">{e.summary}</p>
          <ul className="mt-4 flex flex-col gap-2 text-sm">
            {pointsOf(e)
              .slice(0, 2)
              .map((b) => (
                <li key={b} className="flex gap-2.5">
                  <span className="mt-2 h-1 w-3 shrink-0 rounded-full bg-hl" aria-hidden />
                  {b}
                </li>
              ))}
          </ul>
          <span className="mt-auto pt-6 text-sm font-semibold text-ink">
            Read more{" "}
            <span className="inline-block transition-transform group-hover:translate-x-1">→</span>
          </span>
        </Link>
      ))}
    </div>
  );
}

export function SectionIndex({ k }: { k: SectionKey }) {
  const s = SECTIONS[k];
  usePageMeta(`${s.label} · ninekeep`, s.intro);
  return (
    <SiteLayout>
      <Wrap className="pt-10 pb-16">
        <SectionTabs />
        <div className="mt-12">
          <Kicker>{s.label}</Kicker>
          <Display as="h1" className="mt-5 max-w-4xl">
            {s.intro}
          </Display>
          <p className="mt-5 max-w-2xl text-lg text-muted">{s.lead}</p>
        </div>
        <div className="mt-12">
          <EntryCards items={s.items} base={s.path} />
        </div>
      </Wrap>
      <section className="border-t border-line bg-surface">
        <Wrap className="py-16">
          <h2 className="font-display text-2xl font-bold tracking-tight">
            How it all fits together
          </h2>
          <p className="mt-2 max-w-2xl text-muted">
            Read your contracts, monitor your vendors, claim what they owe.
          </p>
          <div className="mt-6">
            <Steps />
          </div>
          <Link to="/how-it-works" className="mt-6 inline-block font-semibold text-accent">
            See the whole journey →
          </Link>
        </Wrap>
      </section>
      <Cta />
    </SiteLayout>
  );
}

const VISUALS: Record<Visual, () => ReactNode> = {
  memo: () => <CreditMemo />,
  clause: () => <ClauseCard />,
  deadlines: () => <DeadlineCard />,
  scorecard: () => <ScorecardCard />,
  portfolio: () => <PortfolioCard />,
  sorted: () => <SortedCard />,
  checks: () => <ChecksCard />,
  recovered: () => <RecoveredCard />,
  small: () => <SmallPlanCard />,
};

/** Where to go next from a page: the product parts for a problem, the problems for a part. */
const relatedTo = (k: SectionKey, slug: string): [string, Entry][] =>
  k === "product"
    ? SOLUTIONS.slice(0, 3).map((e) => [`/solutions/${e.slug}`, e])
    : PRODUCTS.filter((e) => e.slug !== slug)
        .slice(0, 3)
        .map((e) => [`/product/${e.slug}`, e]);

export function EntryPage({ k }: { k: SectionKey }) {
  const s = SECTIONS[k];
  const { slug } = useParams();
  const items = s.items as readonly Entry[];
  const at = items.findIndex((i) => i.slug === slug);
  const e = items[at];
  usePageMeta(e ? `${e.title} · ninekeep` : "ninekeep", e?.summary ?? s.intro);
  if (!e) return <Navigate to={s.path} replace />;
  const others = items.filter((i) => i.slug !== e.slug);
  const prev = items[at - 1];
  const next = items[at + 1];
  const visual = e.visual ? VISUALS[e.visual]() : null;
  return (
    <SiteLayout>
      <Wrap className="pt-10 pb-16">
        <SectionTabs />
        <nav
          className="mt-10 flex items-center gap-2 font-mono text-xs tracking-label text-faint uppercase"
          aria-label="Breadcrumb"
        >
          <Link to={s.path} className="hover:text-ink">
            ← {s.label}
          </Link>
          <span aria-hidden>/</span>
          <span className="text-muted">{e.title}</span>
        </nav>
        <div className="mt-6 grid items-center gap-12 lg:grid-cols-[1.1fr_1fr]">
          <div className="min-w-0">
            {e.coming && (
              <span className="mb-4 inline-block rounded-full bg-surface-2 px-2.5 py-0.5 font-mono text-label tracking-label text-muted uppercase">
                Coming
              </span>
            )}
            <Display as="h1" className="max-w-3xl">
              {e.title}
            </Display>
            <p className="mt-6 max-w-2xl text-xl text-muted">{e.lead}</p>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <Link
                to="/waitlist"
                className="inline-flex h-11 items-center rounded-control bg-ink px-5 font-semibold text-bg"
              >
                Get started free
              </Link>
              <Link to="/how-it-works" className="font-semibold text-accent">
                See how it works →
              </Link>
            </div>
          </div>
          {visual && <div className="min-w-0">{visual}</div>}
        </div>
      </Wrap>

      {e.highlights && (
        <section className="bg-band text-band-ink">
          <Wrap className="grid gap-px py-14 md:grid-cols-3 md:gap-10">
            {e.highlights.map(([t, b], i) => (
              <div key={t} className="py-4 md:py-0">
                <span className="tabular flex size-8 items-center justify-center rounded-full bg-hl font-mono text-sm font-semibold text-hl-ink">
                  {i + 1}
                </span>
                <h2 className="mt-4 font-display text-2xl font-bold tracking-tight">{t}</h2>
                <p className="mt-2 text-band-muted">{b}</p>
              </div>
            ))}
          </Wrap>
        </section>
      )}

      {e.sections.length > 0 && (
        <Wrap className="py-16">
          <div className="grid gap-4 md:grid-cols-2">
            {e.sections.map((sec) => (
              <div
                key={sec.heading}
                className={cx(
                  "rounded-panel border border-line bg-surface p-7",
                  e.sections.length === 1 && "md:col-span-2",
                )}
              >
                <h2 className="font-display text-2xl font-bold tracking-tight">{sec.heading}</h2>
                {sec.text && <p className="mt-3 text-muted">{sec.text}</p>}
                {sec.bullets && (
                  <ul className="mt-4 flex flex-col gap-2.5">
                    {sec.bullets.map((b) => (
                      <li key={b} className="flex gap-3">
                        <span className="mt-2 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
                        {b}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Wrap>
      )}

      <section className="border-t border-line bg-surface">
        <Wrap className="py-16">
          <h2 className="font-display text-2xl font-bold tracking-tight">How ninekeep does it</h2>
          <div className="mt-6">
            <Steps />
          </div>
          <Link to="/how-it-works" className="mt-6 inline-block font-semibold text-accent">
            See the whole journey →
          </Link>
        </Wrap>
      </section>

      {e.faq && (
        <Wrap className="grid gap-10 py-16 lg:grid-cols-[1fr_2fr]">
          <h2 className="font-display text-3xl font-bold tracking-tight">Questions</h2>
          <div className="divide-y divide-line border-y border-line">
            {e.faq.map(([q, a]) => (
              <details key={q} className="group py-4">
                <summary className="flex cursor-pointer list-none items-baseline justify-between gap-6 font-display text-lg font-semibold tracking-tight [&::-webkit-details-marker]:hidden">
                  {q}
                  <span
                    className="text-faint transition-transform group-open:rotate-45"
                    aria-hidden
                  >
                    +
                  </span>
                </summary>
                <p className="mt-2 text-muted">{a}</p>
              </details>
            ))}
          </div>
        </Wrap>
      )}

      <Wrap className="pb-16">
        <h2 className="mb-5 font-display text-2xl font-bold tracking-tight">Related</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {relatedTo(k, e.slug).map(([to, r]) => (
            <Link
              key={to}
              to={to}
              className="group flex flex-col gap-2 rounded-panel border border-line bg-surface p-6 transition-colors hover:border-ink"
            >
              <h3 className="font-display text-xl font-bold tracking-tight group-hover:text-accent">
                {r.title}
              </h3>
              <p className="text-sm text-muted">{r.summary}</p>
            </Link>
          ))}
        </div>
        <PrevNext
          prev={prev && { to: `${s.path}/${prev.slug}`, title: prev.title }}
          next={next && { to: `${s.path}/${next.slug}`, title: next.title }}
        />
      </Wrap>
      <section className="border-t border-line bg-surface">
        <Wrap className="py-16">
          <h2 className="mb-6 font-display text-2xl font-bold tracking-tight">
            More in {s.label.toLowerCase()}
          </h2>
          <LinkList items={others} base={s.path} />
        </Wrap>
      </section>
      <Cta />
    </SiteLayout>
  );
}

/** The previous and next page in a series: posts, features, industries. */
function PrevNext({
  prev,
  next,
}: {
  prev?: { to: string; title: string };
  next?: { to: string; title: string };
}) {
  if (!prev && !next) return null;
  const card =
    "group flex min-w-0 flex-1 flex-col gap-1 rounded-panel border border-line bg-surface p-5 transition-colors hover:border-ink";
  return (
    <nav aria-label="More pages" className="mt-14 flex flex-col gap-4 sm:flex-row">
      {prev ? (
        <Link to={prev.to} className={card}>
          <span className="eyebrow">← Previous</span>
          <span className="font-display text-lg font-bold tracking-tight group-hover:text-accent">
            {prev.title}
          </span>
        </Link>
      ) : (
        <span className="hidden flex-1 sm:block" />
      )}
      {next ? (
        <Link to={next.to} className={cx(card, "sm:items-end sm:text-right")}>
          <span className="eyebrow">Next →</span>
          <span className="font-display text-lg font-bold tracking-tight group-hover:text-accent">
            {next.title}
          </span>
        </Link>
      ) : (
        <span className="hidden flex-1 sm:block" />
      )}
    </nav>
  );
}

export function VendorsIndex() {
  usePageMeta(
    "Vendors ninekeep works with · ninekeep",
    "Any software or cloud vendor whose contract promises uptime.",
  );
  const groups = [...new Set(VENDORS.map((v) => v.category))];
  return (
    <SiteLayout>
      <Wrap className="py-16">
        <Kicker>Vendors</Kicker>
        <Display as="h1" className="mt-5 max-w-4xl">
          If the contract promises uptime, ninekeep can read it.
        </Display>
        <p className="mt-6 max-w-2xl text-lg text-muted">
          These are just examples. ninekeep works with any vendor. Where a vendor publishes its
          terms, we show them here. Your own contract may differ, and that's the one ninekeep uses.
        </p>
        <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
          {groups.map((g) => (
            <div key={g} className="min-w-0 border-t-2 border-ink pt-4">
              <h2 className="eyebrow">{g}</h2>
              <ul className="mt-3 flex flex-col">
                {VENDORS.filter((v) => v.category === g).map((v) => (
                  <li key={v.slug}>
                    <Link
                      to={`/supported-vendors/${v.slug}`}
                      className="flex items-center gap-2 py-1.5 font-medium hover:text-accent"
                    >
                      {v.name}
                      {v.published && (
                        <span className="mark-clause font-mono text-label">public terms</span>
                      )}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Wrap>
      <Cta />
    </SiteLayout>
  );
}

export function VendorPage() {
  const { slug } = useParams();
  const v: Vendor | undefined = VENDORS.find((x) => x.slug === slug);
  usePageMeta(
    v ? `${v.name} SLA credits · ninekeep` : "ninekeep",
    v ? `How ninekeep claims SLA credits from ${v.name}.` : "",
  );
  if (!v) return <Navigate to="/supported-vendors" replace />;
  const p = v.published;
  const short = v.name.split(" (")[0];
  return (
    <SiteLayout>
      <Wrap className="py-16">
        <nav
          className="font-mono text-xs tracking-label text-faint uppercase"
          aria-label="Breadcrumb"
        >
          <Link to="/supported-vendors" className="hover:text-ink">
            Vendors
          </Link>{" "}
          · {v.category}
        </nav>
        <Display as="h1" className="mt-5 max-w-4xl">
          {v.name} SLA credits
        </Display>
        <p className="mt-6 max-w-2xl text-lg text-muted">
          ninekeep reads the contract you signed with {short}, monitors the services it covers and
          claims any credit owed before the deadline.
        </p>
        {p ? (
          <div className="memo mt-12 max-w-3xl rounded-b-card px-7 pt-9 pb-7 shadow-hero">
            <div className="flex flex-wrap items-baseline justify-between gap-2 eyebrow">
              <span>Published terms</span>
              <a
                href={p.source}
                target="_blank"
                rel="noreferrer"
                className="normal-case tracking-normal text-accent"
              >
                {p.source.replace("https://", "")} ↗
              </a>
            </div>
            <dl className="mt-5 grid gap-4 sm:grid-cols-[150px_1fr]">
              <dt className="font-mono text-xs text-muted uppercase">Promise</dt>
              <dd>
                <mark className="mark-clause">{p.promise}</mark>
              </dd>
              <dt className="font-mono text-xs text-muted uppercase">Credits</dt>
              <dd>
                <ul className="flex flex-col gap-1">
                  {p.credits.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </dd>
              <dt className="font-mono text-xs text-muted uppercase">Deadline</dt>
              <dd>
                <mark className="mark-clause">{p.deadline}</mark>
              </dd>
              <dt className="font-mono text-xs text-muted uppercase">How to claim</dt>
              <dd>{p.howToClaim}</dd>
            </dl>
            {p.note && <p className="mt-5 text-sm text-muted">{p.note}</p>}
            <p className="mt-5 font-mono text-label text-faint">
              As published, checked {checkedOn(p.checked)}. Negotiated contracts often differ;
              ninekeep uses yours.
            </p>
            <div className="mt-2">
              <WatchedAt v={v} />
            </div>
          </div>
        ) : (
          <div className="mt-12 max-w-3xl border-t-2 border-ink pt-5">
            <h2 className="font-display text-2xl font-bold tracking-tight">
              What ninekeep reads in your {short} agreement
            </h2>
            <div className="mt-4">
              <ContractTerms v={v} />
            </div>
            <div className="mt-5">
              <WatchedAt v={v} />
            </div>
          </div>
        )}
      </Wrap>
      <section className="border-t border-line bg-surface">
        <Wrap className="py-16">
          <h2 className="mb-6 font-display text-2xl font-bold tracking-tight">Other vendors</h2>
          <VendorStrip />
        </Wrap>
      </section>
      <Cta />
    </SiteLayout>
  );
}

// ---------------------------------------------------------------------------------- blog

const longDate = (iso: string) =>
  new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

function PostMeta({ p }: { p: Post }) {
  return (
    <p className="font-mono text-xs tracking-label text-faint uppercase">
      {longDate(p.date)} · {p.minutes} min read · The ninekeep team
    </p>
  );
}

/** Share a post as itself: the link carries its own title and summary in the preview. */
function ShareBar({ p }: { p: Post }) {
  const [copied, setCopied] = useState(false);
  const url = `${location.origin}${postPath(p)}`;
  const enc = encodeURIComponent;
  const linkCls =
    "inline-flex h-9 items-center rounded-control border border-line bg-surface px-3.5 text-sm font-medium hover:border-ink";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="mr-1 eyebrow">Share</span>
      <a
        className={linkCls}
        target="_blank"
        rel="noreferrer"
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`}
      >
        LinkedIn
      </a>
      <a
        className={linkCls}
        target="_blank"
        rel="noreferrer"
        href={`https://x.com/intent/post?url=${enc(url)}&text=${enc(p.title)}`}
      >
        X
      </a>
      <button
        type="button"
        className={linkCls}
        onClick={() => {
          navigator.clipboard?.writeText(url).then(
            () => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1600);
            },
            () => {},
          );
        }}
      >
        {copied ? "Link copied" : "Copy link"}
      </button>
    </div>
  );
}

const anchor = (h: string) =>
  h
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");

/** The post's headings, to jump to. */
function Contents({ blocks }: { blocks: Block[] }) {
  const hs = blocks.flatMap((b) => ("h" in b ? [b.h] : []));
  if (hs.length < 2) return null;
  return (
    <nav aria-label="In this post">
      <p className="eyebrow">In this post</p>
      <ol className="mt-3 flex flex-col gap-2 border-l border-line">
        {hs.map((h) => (
          <li key={h}>
            <a
              href={`#${anchor(h)}`}
              className="-ml-px block border-l-2 border-transparent pl-4 text-sm text-muted hover:border-ink hover:text-ink"
            >
              {h}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function PostBody({ blocks }: { blocks: Block[] }) {
  return (
    <div className="flex flex-col gap-5 text-prose">
      {blocks.map((b) => {
        const k = JSON.stringify(b).slice(0, 80);
        if ("h" in b)
          return (
            <h2
              key={k}
              id={anchor(b.h)}
              className="mt-6 scroll-mt-24 font-display text-2xl font-bold tracking-tight"
            >
              {b.h}
            </h2>
          );
        if ("p" in b) return <p key={k}>{b.p}</p>;
        if ("note" in b)
          return (
            <p
              key={k}
              className="rounded-card border border-line bg-surface px-5 py-4 text-base text-muted"
            >
              {b.note}
            </p>
          );
        if ("ul" in b || "ol" in b) {
          const items = "ul" in b ? b.ul : b.ol;
          return (
            <ul key={k} className="flex flex-col gap-2.5">
              {items.map((t, j) => (
                <li key={t} className="flex gap-3">
                  {"ol" in b ? (
                    <span className="tabular mt-1 flex size-6 shrink-0 items-center justify-center rounded-full bg-hl font-mono text-xs font-semibold text-hl-ink">
                      {j + 1}
                    </span>
                  ) : (
                    <span className="mt-3 h-1 w-4 shrink-0 rounded-full bg-hl" aria-hidden />
                  )}
                  <span className="min-w-0">{t}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <div key={k} className="overflow-x-auto rounded-card border border-line bg-surface">
            <table className="w-full text-left text-base">
              <thead className="border-b border-line bg-surface-2 font-mono text-label tracking-label text-muted uppercase">
                <tr>
                  {b.head.map((h) => (
                    <th key={h} className="px-4 py-2.5 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {b.rows.map((r) => (
                  <tr key={r.join("|")}>
                    {b.head.map((h, j) => (
                      <td
                        key={h || "first"}
                        className={cx("px-4 py-3 align-top", j === 0 && "font-medium")}
                      >
                        {r[j]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}

export function BlogIndex() {
  usePageMeta(
    "The ninekeep blog",
    "Simple guides to getting money back from your vendors: reading the contract, meeting the deadline, getting paid.",
  );
  const [first, ...rest] = POSTS;
  return (
    <SiteLayout>
      <Wrap className="py-16">
        <Kicker>Blog</Kicker>
        <Display as="h1" className="mt-5 max-w-3xl">
          SLA credits, in plain words.
        </Display>
        <p className="mt-5 max-w-2xl text-lg text-muted">
          Read them in order and you'll know how to find, claim and collect every credit your
          vendors owe. Each takes a few minutes.
        </p>
        {first && (
          <Link
            to={postPath(first)}
            className="group mt-12 block rounded-panel bg-band p-8 text-band-ink sm:p-10"
          >
            <p className="flex items-center gap-3 font-mono text-xs tracking-label text-band-muted uppercase">
              <span className="tabular flex size-7 items-center justify-center rounded-full bg-hl font-semibold text-hl-ink">
                1
              </span>
              Start here · {first.minutes} min read
            </p>
            <h2 className="mt-4 max-w-3xl font-display text-3xl font-bold tracking-tight text-balance sm:text-4xl">
              {first.title}
            </h2>
            <p className="mt-3 max-w-2xl text-band-muted">{first.dek}</p>
            <span className="mt-6 inline-block font-semibold text-hl">Read it →</span>
          </Link>
        )}
        <div className="mt-6 grid gap-4 md:grid-cols-2">
          {rest.map((p, i) => (
            <Link
              key={p.slug}
              to={postPath(p)}
              className="group flex flex-col gap-3 rounded-panel border border-line bg-surface p-7 transition-colors hover:border-ink"
            >
              <span className="flex items-center gap-3">
                <span className="tabular flex size-7 items-center justify-center rounded-full bg-hl font-mono text-xs font-semibold text-hl-ink">
                  {i + 2}
                </span>
                <PostMeta p={p} />
              </span>
              <h2 className="font-display text-2xl font-bold tracking-tight text-balance group-hover:text-accent">
                {p.title}
              </h2>
              <p className="text-muted">{p.dek}</p>
            </Link>
          ))}
        </div>
      </Wrap>
      <Cta />
    </SiteLayout>
  );
}

export function PostPage() {
  const { slug } = useParams();
  const at = POSTS.findIndex((x) => x.slug === slug);
  const p = POSTS[at];
  usePageMeta(p ? `${p.title} · ninekeep` : "ninekeep", p?.dek ?? "");
  if (!p) return <Navigate to="/resources" replace />;
  const prev = POSTS[at - 1];
  const next = POSTS[at + 1];
  const more = POSTS.filter((x) => x.slug !== p.slug);
  return (
    <SiteLayout>
      <Wrap className="py-16">
        <nav
          className="flex items-center gap-2 font-mono text-xs tracking-label text-faint uppercase"
          aria-label="Breadcrumb"
        >
          <Link
            to="/resources"
            className="inline-flex h-8 items-center gap-1.5 rounded-full border border-line bg-surface px-3.5 normal-case tracking-normal font-sans text-sm font-medium text-ink hover:border-ink"
          >
            ← All posts
          </Link>
          <span className="ml-2 text-muted">
            Post {at + 1} of {POSTS.length}
          </span>
        </nav>
        <div className="mt-5 grid gap-12 lg:grid-cols-[minmax(0,1fr)_16rem]">
          <article className="min-w-0 max-w-3xl">
            <h1 className="font-display text-4xl leading-display font-bold tracking-tight text-balance sm:text-5xl">
              {p.title}
            </h1>
            <p className="mt-5 text-xl text-muted">{p.dek}</p>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-y border-line py-4">
              <PostMeta p={p} />
              <ShareBar p={p} />
            </div>
            <div className="mt-8 lg:hidden">
              <Contents blocks={p.body} />
            </div>
            <div className="mt-10">
              <PostBody blocks={p.body} />
            </div>
            <div className="mt-14 rounded-panel bg-band p-8 text-band-ink">
              <h2 className="font-display text-2xl font-bold tracking-tight">
                See what your vendors owe you
              </h2>
              <p className="mt-2 text-band-muted">
                Start with a free audit: up to 5 contracts and the last 12 months, with the claims
                ready to send.
              </p>
              <Link
                to="/waitlist"
                className="mt-5 inline-flex h-11 items-center rounded-control bg-hl px-5 font-semibold text-hl-ink"
              >
                Get started
              </Link>
            </div>
            <PrevNext
              prev={prev && { to: postPath(prev), title: prev.title }}
              next={next && { to: postPath(next), title: next.title }}
            />
          </article>
          <aside className="hidden lg:block">
            <div className="sticky top-24 flex flex-col gap-10">
              <Contents blocks={p.body} />
              <div>
                <p className="eyebrow">All posts</p>
                <ol className="mt-3 flex flex-col gap-2.5">
                  {POSTS.map((x, i) => (
                    <li key={x.slug}>
                      <Link
                        to={postPath(x)}
                        aria-current={x.slug === p.slug ? "page" : undefined}
                        className={cx(
                          "flex gap-2.5 text-sm",
                          x.slug === p.slug
                            ? "font-semibold text-ink"
                            : "text-muted hover:text-ink",
                        )}
                      >
                        <span className="tabular font-mono text-faint">{i + 1}</span>
                        {x.title}
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </aside>
        </div>
      </Wrap>
      <section className="border-t border-line bg-surface">
        <Wrap className="py-16">
          <div className="mb-6 flex items-baseline justify-between gap-4">
            <h2 className="font-display text-2xl font-bold tracking-tight">Keep reading</h2>
            <Link to="/resources" className="text-sm font-semibold text-accent">
              All posts →
            </Link>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {more.map((x) => (
              <Link
                key={x.slug}
                to={postPath(x)}
                className="group flex flex-col gap-2 rounded-panel border border-line bg-bg p-6 hover:border-ink"
              >
                <PostMeta p={x} />
                <h3 className="font-display text-xl font-bold tracking-tight group-hover:text-accent">
                  {x.title}
                </h3>
                <p className="text-sm text-muted">{x.dek}</p>
              </Link>
            ))}
          </div>
        </Wrap>
      </section>
    </SiteLayout>
  );
}
